import { cn } from '@heidi/utils';
import { cva, type VariantProps } from 'class-variance-authority';
import { typographyVariant } from '../../../typography';

// ============================================================================
// Sidebar Provider Styles
// ============================================================================

/**
 * Changed from `min-h-screen` to `min-h-0 flex-1` to temporarily support MobileSidebar usage.
 *
 * MobileSidebar wraps its content in its own SidebarProvider with `!min-h-0 !flex-none`
 * overrides, but using `flex-1` here allows the provider to expand naturally within
 * PageShell while not forcing full viewport height when nested in constrained containers.
 *
 * TODO(@jordan): Remove this once MobileSidebar is removed (scribe-layout restructure).
 */
export const sidebarProviderClasses = cn(
  'group/sidebar-wrapper flex min-h-0 w-full flex-1 has-[[data-variant=inset]]:bg-surface-accent'
);

// ============================================================================
// Sidebar Styles
// ============================================================================

export const sidebarBaseClasses = cn(
  'flex h-full w-[var(--sidebar-width)] flex-col bg-surface-accent text-text-secondary'
);

export const sidebarDesktopWrapperClasses = cn(
  'group/sidebar group peer relative hidden text-text-secondary sm:block'
);

/**
 * Mobile drawer content container.
 */
export const sidebarMobileContentClasses = cn(
  // PanelMobile below uses `size-full` on its container, with absolute-inset-0
  // children — `h-full` needs an explicit parent height to resolve, so the
  // drawer keeps a fixed viewport-relative height (not min/max) or the inner
  // content collapses to zero and the drawer renders empty.
  'group h-[85dvh] bg-surface-accent p-0 text-text-secondary'
);

/**
 * Mobile sidebar inner container.
 * Note: PanelMobile handles its own overflow clipping for the slider animation.
 */
export const sidebarMobileInnerClasses = cn('flex size-full flex-col');

export const sidebarGapVariants = cva(
  cn(
    'relative bg-transparent transition-[width] duration-200 ease-out',
    'motion-reduce:transition-none'
  ),
  {
    variants: {
      collapsible: {
        none: 'w-[var(--sidebar-width)]',
        offcanvas: 'w-0',
        icon: 'w-[var(--sidebar-width-icon)]',
        'label-icons': 'w-[var(--sidebar-width-label-icons)]',
      },
      variant: {
        sidebar: '',
        floating: '',
        inset: '',
      },
    },
    compoundVariants: [
      // Floating expanded (+1rem padding + 2px border)
      {
        variant: 'floating',
        collapsible: 'none',
        class: '!w-[calc(var(--sidebar-width)+1rem+2px)]',
      },
      // Floating icon collapsed
      {
        variant: 'floating',
        collapsible: 'icon',
        class: '!w-[calc(var(--sidebar-width-icon)+1rem+2px)]',
      },
      // Floating label-icons collapsed
      {
        variant: 'floating',
        collapsible: 'label-icons',
        class: '!w-[calc(var(--sidebar-width-label-icons)+1rem+2px)]',
      },
      // Inset expanded (+1rem padding, no border)
      {
        variant: 'inset',
        collapsible: 'none',
        class: '!w-[calc(var(--sidebar-width)+1rem)]',
      },
      // Inset icon collapsed
      {
        variant: 'inset',
        collapsible: 'icon',
        class: '!w-[calc(var(--sidebar-width-icon)+1rem)]',
      },
      // Inset label-icons collapsed — drop the +1rem so the ribbon hugs the inset edge
      {
        variant: 'inset',
        collapsible: 'label-icons',
        class: '!w-[var(--sidebar-width-label-icons)]',
      },
    ],
    defaultVariants: {
      collapsible: 'none',
      variant: 'sidebar',
    },
  }
);

export const sidebarContainerVariants = cva(
  cn(
    // Note: h-svh (small viewport height) ensures sidebar fits within visible area on all devices
    'fixed inset-y-0 start-0 z-10 hidden h-svh transition-[left,right,width] duration-200 ease-out sm:flex',
    'motion-reduce:transition-none'
  ),
  {
    variants: {
      collapsible: {
        none: 'w-[var(--sidebar-width)]',
        offcanvas: 'start-[calc(var(--sidebar-width)*-1)]',
        icon: 'w-[var(--sidebar-width-icon)] overflow-hidden',
        'label-icons': 'w-[var(--sidebar-width-label-icons)] overflow-hidden',
      },
      variant: {
        sidebar: 'border-e',
        floating: 'p-2',
        inset: 'px-2 py-1',
      },
    },
    compoundVariants: [
      // Label-icons mode is always collapsed
      {
        variant: 'sidebar',
        collapsible: 'label-icons',
        class: 'border-none',
      },
      // Floating expanded (+1rem padding + 2px border)
      {
        variant: 'floating',
        collapsible: 'none',
        class: '!w-[calc(var(--sidebar-width)+1rem+2px)]',
      },
      // Floating icon collapsed
      {
        variant: 'floating',
        collapsible: 'icon',
        class: '!w-[calc(var(--sidebar-width-icon)+1rem+2px)]',
      },
      // Floating label-icons collapsed
      {
        variant: 'floating',
        collapsible: 'label-icons',
        class: '!w-[calc(var(--sidebar-width-label-icons)+1rem+2px)]',
      },
      // Inset expanded (+1rem padding, no border)
      {
        variant: 'inset',
        collapsible: 'none',
        class: '!w-[calc(var(--sidebar-width)+1rem)]',
      },
      // Inset icon collapsed
      {
        variant: 'inset',
        collapsible: 'icon',
        class: '!w-[calc(var(--sidebar-width-icon)+1rem)]',
      },
      // Inset label-icons collapsed — drop the +1rem and remove the inset px-2 so the ribbon sits flush
      {
        variant: 'inset',
        collapsible: 'label-icons',
        class: '!w-[var(--sidebar-width-label-icons)] !px-0',
      },
    ],
    defaultVariants: {
      collapsible: 'none',
      variant: 'sidebar',
    },
  }
);

export const sidebarInnerVariants = cva(
  // overflow-y-hidden: prevent vertical scrolling at this level (SidebarContent handles it)
  // overflow-x-visible: allows NavigationMenu viewport to render outside sidebar
  'flex size-full flex-col overflow-y-hidden overflow-x-visible bg-surface-accent',
  {
    variants: {
      variant: {
        sidebar: '',
        floating: 'rounded-lg border border-border shadow-sm',
        inset: '',
      },
    },
    defaultVariants: {
      variant: 'sidebar',
    },
  }
);

// ============================================================================
// Sidebar Rail Styles
// ============================================================================

export const sidebarRailVariants = cva(
  cn(
    'absolute inset-y-0 -end-4 z-20 hidden w-4 -translate-x-1/2 cursor-w-resize sm:flex rtl:translate-x-1/2 rtl:cursor-e-resize',
    'transition-[transform,background-color] duration-200 ease-out',
    'motion-reduce:transition-none',
    'after:absolute after:inset-y-0 after:start-1/2 after:w-[2px]',
    'hover:after:bg-border',
    'focus:outline-none focus-visible:after:bg-border-focus'
  ),
  {
    variants: {
      state: {
        expanded: '',
        collapsed: 'cursor-e-resize rtl:cursor-w-resize',
      },
      collapsible: {
        none: '',
        offcanvas: '-end-2 translate-x-0 after:start-full hover:bg-surface',
        icon: '',
        'label-icons': '',
      },
    },
    defaultVariants: {
      state: 'expanded',
      collapsible: 'none',
    },
  }
);

// ============================================================================
// Sidebar Inset Styles
// ============================================================================

export const sidebarInsetClasses = (
  variant: 'sidebar' | 'floating' | 'inset' = 'sidebar'
) =>
  cn(
    'relative flex w-full flex-1 flex-col bg-surface',
    variant === 'inset' && 'sm:m-3 sm:ms-0 sm:w-auto sm:overflow-hidden sm:rounded-lg'
  );

// ============================================================================
// Sidebar Input Styles
// ============================================================================

export const sidebarInputClasses = cn('h-8 w-full bg-surface shadow-none');

// ============================================================================
// Sidebar Header/Footer Styles
// ============================================================================

export const sidebarHeaderVariants = cva('flex flex-col gap-2 p-2', {
  variants: {
    collapsible: {
      none: '',
      offcanvas: '',
      icon: '',
      'label-icons': 'pt-3',
    },
  },
  defaultVariants: {
    collapsible: 'none',
  },
});

export const sidebarFooterVariants = cva('flex flex-col gap-2 p-2', {
  variants: {
    collapsible: {
      none: '',
      offcanvas: '',
      icon: 'mt-8',
      'label-icons': 'mt-auto pb-3',
    },
  },
  defaultVariants: {
    collapsible: 'none',
  },
});

// ============================================================================
// Sidebar Action Styles
// ============================================================================

export const sidebarActionVariants = cva(
  cn(
    'my-2 px-2',
    // Button layout
    '[&_button]:relative [&_button]:w-full [&_button]:!justify-start [&_button]:!gap-2 [&_button]:overflow-hidden [&_button]:!rounded-md [&_button]:!p-2 [&_button]:text-start',
    '[&_button]:transition-[width,height,padding] [&_button]:duration-200 [&_button]:ease-out',
    '[&_button]:motion-reduce:transition-none',
    // Icon sizing and transition for smooth centering animation
    '[&_button_svg]:!size-4 [&_button_svg]:shrink-0',
    '[&_button_svg]:transition-transform [&_button_svg]:duration-200 [&_button_svg]:ease-out',
    '[&_button_svg]:motion-reduce:transform-none [&_button_svg]:motion-reduce:transition-none',
    // Text spans
    '[&_button>span]:shrink-0 [&_button>span]:truncate [&_button>span]:whitespace-nowrap',
    '[&_button>span]:transition-[opacity,transform] [&_button>span]:duration-200 [&_button>span]:ease-out',
    '[&_button>span]:motion-reduce:transform-none [&_button>span]:motion-reduce:transition-none'
  ),
  {
    variants: {
      collapsible: {
        none: '',
        offcanvas: '',
        icon: cn(
          '[&_button]:!h-8 [&_button]:!w-8 [&_button]:!p-2',
          '[&_button>span]:-translate-x-0.5 [&_button>span]:opacity-0'
        ),
        // Note: label-icons mode uses SidebarMenuButton directly, not SidebarAction
        'label-icons': '',
      },
      mobile: {
        true: cn(
          '[&_button]:!h-10 [&_button]:!gap-1.5 [&_button]:!px-3',
          '[&_button_svg]:!size-4.5'
        ),
        false: '',
      },
    },
    defaultVariants: {
      collapsible: 'none',
      mobile: false,
    },
  }
);

// ============================================================================
// Sidebar Separator Styles
// ============================================================================

export const sidebarSeparatorClasses = cn('mx-2 w-auto bg-border');

// ============================================================================
// Sidebar Content Styles
// ============================================================================

export const sidebarContentVariants = cva(
  // overflow-y-auto: enable vertical scrolling for content
  // overflow-x-visible: CRITICAL - allows NavigationMenu viewport to render outside sidebar
  // TODO(@jordan): Remove overflow-x-hidden once we have a way to handle the overflow-x-visible issue
  'flex min-h-0 flex-1 flex-col gap-2 overflow-y-auto overflow-x-hidden',
  {
    variants: {
      collapsible: {
        none: '',
        offcanvas: '',
        icon: '',
        'label-icons': 'gap-0',
      },
    },
    defaultVariants: {
      collapsible: 'none',
    },
  }
);

// ============================================================================
// Sidebar Group Styles
// ============================================================================

export const sidebarGroupClasses = cn(
  'relative flex w-full min-w-0 flex-col p-2',
  'group-data-[collapsible=label-icons]/sidebar:px-1'
);

export const sidebarGroupLabelVariants = cva(
  cn(
    'flex h-8 items-center overflow-hidden whitespace-nowrap rounded-md px-2',
    'text-xs font-medium text-text-tertiary',
    'outline-none ring-border-focus focus-visible:ring-2',
    '[&>svg]:size-4 [&>svg]:shrink-0',
    'transition-[opacity,transform,height] duration-200 ease-out',
    'motion-reduce:transform-none motion-reduce:transition-none'
  ),
  {
    variants: {
      collapsible: {
        none: '',
        offcanvas: '',
        icon: 'h-0 -translate-x-0.5 opacity-0',
        'label-icons': 'h-0 -translate-x-0.5 opacity-0',
      },
      mobile: {
        true: 'h-10 px-3',
        false: '',
      },
    },
    defaultVariants: {
      collapsible: 'none',
      mobile: false,
    },
  }
);

export const sidebarGroupActionVariants = cva(
  cn(
    'absolute end-3 top-3.5 flex aspect-square w-5 items-center justify-center rounded-md p-0',
    'text-text-secondary',
    'outline-none ring-border-focus transition-transform',
    'hover:bg-background-secondary-hover focus-visible:ring-2',
    '[&>svg]:size-4 [&>svg]:shrink-0',
    'after:absolute after:-inset-2 sm:after:hidden'
  ),
  {
    variants: {
      collapsible: {
        none: '',
        offcanvas: '',
        icon: 'hidden',
        'label-icons': 'hidden',
      },
    },
    defaultVariants: {
      collapsible: 'none',
    },
  }
);

export const sidebarGroupContentClasses = cn(
  'w-full',
  typographyVariant({ variant: 'captionBold' })
);

// ============================================================================
// Sidebar Menu Styles
// ============================================================================

export const sidebarMenuVariants = cva('flex w-full min-w-0 list-none flex-col gap-1', {
  variants: {
    collapsible: {
      none: '',
      offcanvas: '',
      icon: '',
      'label-icons': 'gap-1',
    },
  },
  defaultVariants: {
    collapsible: 'none',
  },
});

export const sidebarMenuItemClasses = cn('group/menu-item relative list-none');

// ============================================================================
// Sidebar Menu Button CVA Variants
// ============================================================================

export const sidebarMenuButtonVariantClasses = {
  default: 'hover:bg-background-secondary-hover',
  emphasis:
    'bg-surface shadow-[0_0_0_1px_hsl(var(--border))] hover:bg-background-secondary-hover hover:shadow-[0_0_0_1px_hsl(var(--border-hover))]',
  action:
    'bg-background-button-primary text-text-primary-invert hover:bg-background-button-primary-hover active:bg-background-button-primary-pressed disabled:bg-background-button-primary-disabled',
} as const;

export const sidebarMenuButtonSizeClasses = {
  default: 'h-8',
  sm: 'h-7',
  lg: 'h-12',
} as const;

export const sidebarMenuButtonVariants = cva(
  cn(
    typographyVariant({ variant: 'captionBold' }),
    'peer/menu-button relative flex w-full items-center gap-2 overflow-hidden rounded-[10px] p-2 text-start',
    'outline-none ring-border-focus',
    'transition-[width,height,padding] duration-200 ease-out',
    'motion-reduce:transition-none',
    'hover:bg-background-secondary-hover',
    'focus-visible:ring-2',
    'active:bg-background-secondary-pressed active:text-text-primary',
    'disabled:pointer-events-none disabled:opacity-50',
    'aria-disabled:pointer-events-none aria-disabled:opacity-50',
    'group-has-[[data-sidebar=menu-action]]/menu-item:pe-8',
    'data-[active=true]:bg-background-secondary-pressed data-[active=true]:text-text-primary',
    'data-[state=open]:bg-background-secondary-pressed data-[state=open]:text-text-primary',
    'data-[state=open]:hover:bg-background-secondary-hover',
    // Text span styles
    '[&>span:not(:first-child)]:shrink-0 [&>span:not(:first-child)]:truncate [&>span:not(:first-child)]:whitespace-nowrap',
    '[&>svg]:size-4 [&>svg]:shrink-0',
    // SVG transition for smooth icon centering animation
    '[&>svg]:transition-transform [&>svg]:duration-200 [&>svg]:ease-out',
    '[&>svg]:motion-reduce:transform-none [&>svg]:motion-reduce:transition-none',
    '[&>span:not(:first-child)]:transition-[opacity,transform] [&>span:not(:first-child)]:duration-200 [&>span:not(:first-child)]:ease-out',
    '[&>span:not(:first-child)]:motion-reduce:transform-none [&>span:not(:first-child)]:motion-reduce:transition-none'
  ),
  {
    variants: {
      variant: sidebarMenuButtonVariantClasses,
      size: sidebarMenuButtonSizeClasses,
      collapsible: {
        none: '',
        offcanvas: '',
        icon: cn(
          '!h-8 !w-8 !p-2',
          '[&>span:not(:first-child)]:-translate-x-0.5 [&>span:not(:first-child)]:opacity-0'
        ),
        // Note: 'label-icons' mode uses SidebarMenuButtonLabelIcons component
        // (dedicated component with grid layout, no CSS overrides needed)
        'label-icons': '',
      },
      mobile: {
        true: 'h-10 gap-1.5 px-3 [&>svg]:size-4.5',
        false: '',
      },
    },
    compoundVariants: [
      // Mobile size overrides
      { mobile: true, size: 'sm', className: 'h-9' },
      { mobile: true, size: 'lg', className: 'h-12' },
      // Icon-only mobile buttons should be square
      {
        mobile: true,
        className:
          '[&:not(:has(span))]:!w-10 [&:not(:has(span))]:justify-center [&:not(:has(span))]:!p-0',
      },
      // Large size in icon mode
      { size: 'lg', collapsible: 'icon', className: '!p-0' },
      // Note: Action variant in label-icons mode is handled by SidebarMenuButtonLabelIcons
    ],
    defaultVariants: {
      variant: 'default',
      size: 'default',
      collapsible: 'none',
      mobile: false,
    },
  }
);

// ============================================================================
// Sidebar Menu Action Styles
// ============================================================================

export const sidebarMenuActionVariants = cva(
  cn(
    'absolute end-1 top-1/2 flex size-6 -translate-y-1/2 items-center justify-center rounded-md p-0',
    'text-text-secondary',
    'outline-none ring-border-focus transition-transform',
    'hover:bg-background-secondary-hover focus-visible:ring-2',
    '[&>svg]:size-4 [&>svg]:shrink-0',
    'after:absolute after:-inset-2 sm:after:hidden'
  ),
  {
    variants: {
      collapsible: {
        none: '',
        offcanvas: '',
        icon: 'hidden',
        'label-icons': 'hidden',
      },
      mobile: {
        true: 'size-9',
        false: '',
      },
    },
    defaultVariants: {
      collapsible: 'none',
      mobile: false,
    },
  }
);

export const sidebarMenuActionShowOnHoverClasses = cn(
  'group-focus-within/menu-item:opacity-100 group-hover/menu-item:opacity-100',
  'group-hover/sidebar:opacity-100',
  'data-[state=open]:opacity-100',
  'group-data-[panel-open=true]/menu-item:opacity-100',
  // Only hide on larger screens (≥sm) with hover capability (mouse/trackpad)
  // Touch devices and mobile screens always show actions
  'pointer-fine:sm:opacity-0'
);

// ============================================================================
// Sidebar Menu Badge Styles
// ============================================================================

export const sidebarMenuBadgeVariants = cva(
  cn(
    'pointer-events-none absolute end-1 top-1/2 flex h-5 min-w-5 -translate-y-1/2 select-none items-center justify-center rounded-md px-1',
    'text-xs font-medium tabular-nums text-text-secondary',
    'peer-data-[active=true]/menu-button:text-text-primary',
    // Smooth fade transition - delay on enter (expanding), immediate on exit (collapsing)
    'transition-opacity delay-100 duration-200 ease-out',
    'motion-reduce:transition-none'
  ),
  {
    variants: {
      collapsible: {
        none: '',
        offcanvas: '',
        // delay-0 ensures immediate fade-out, duration-100 completes before width transition (200ms)
        icon: 'opacity-0 delay-0 duration-100',
        'label-icons': 'opacity-0 delay-0 duration-100',
      },
    },
    defaultVariants: {
      collapsible: 'none',
    },
  }
);

// ============================================================================
// Sidebar Menu Skeleton Styles
// ============================================================================

export const sidebarMenuSkeletonClasses = cn(
  'flex h-8 items-center gap-2 rounded-[10px] px-2'
);

// ============================================================================
// Sidebar Menu Sub Styles
// ============================================================================

export const sidebarMenuSubVariants = cva(
  'mx-3.5 flex min-w-0 translate-x-px list-none flex-col gap-1 border-s border-border px-2.5 py-0.5',
  {
    variants: {
      collapsible: {
        none: '',
        offcanvas: '',
        icon: 'hidden',
        'label-icons': 'hidden',
      },
    },
    defaultVariants: {
      collapsible: 'none',
    },
  }
);

export const sidebarMenuSubItemClasses = cn('group/menu-sub-item relative list-none');

export const sidebarMenuSubButtonVariants = cva(
  cn(
    typographyVariant({ variant: 'captionBold' }),
    'flex h-7 min-w-0 -translate-x-px items-center gap-2 overflow-hidden rounded-md px-2',
    'text-text-secondary',
    'outline-none ring-border-focus',
    'hover:bg-background-secondary-hover focus-visible:ring-2',
    'active:bg-background-secondary-pressed active:text-text-primary',
    'disabled:pointer-events-none disabled:opacity-50',
    'aria-disabled:pointer-events-none aria-disabled:opacity-50',
    '[&>span:last-child]:truncate',
    '[&>svg]:size-4 [&>svg]:shrink-0 [&>svg]:text-text-secondary',
    'data-[active=true]:bg-background-secondary-pressed data-[active=true]:text-text-primary'
  ),
  {
    variants: {
      size: {
        sm: 'text-xs',
        md: 'text-sm',
      },
      collapsible: {
        none: '',
        offcanvas: '',
        icon: 'hidden',
        'label-icons': 'hidden',
      },
      mobile: {
        true: 'h-9 gap-1.5 px-3 [&>svg]:size-4.5',
        false: '',
      },
    },
    defaultVariants: {
      size: 'md',
      collapsible: 'none',
      mobile: false,
    },
  }
);

// ============================================================================
// Label-Icons Menu Button Styles
// ============================================================================

/**
 * CVA variants for label-icons mode menu button.
 *
 * Separated from sidebarMenuButtonVariants to avoid specificity battles between
 * the horizontal (flex) and vertical (grid) layouts.
 *
 * Two layouts:
 * - `default`: Full-width grid with icon row (44px) + label row (auto)
 * - `iconOnly`: Compact 36px flex button (no label, for footer actions)
 *
 * Both share: ::before pseudo-element for hover bg, icon size, focus ring.
 */
export const sidebarMenuButtonLabelIconsVariants = cva(
  cn(
    // Shared base: transparent bg, focus ring, disabled, icon sizing
    'relative bg-transparent',
    'before:absolute before:rounded-[10px]',
    'outline-none ring-border-focus focus-visible:ring-2',
    'disabled:pointer-events-none disabled:opacity-50',
    'aria-disabled:pointer-events-none aria-disabled:opacity-50',
    '[&>svg]:z-10 [&>svg]:size-4.5'
  ),
  {
    variants: {
      layout: {
        default: cn(
          typographyVariant({ variant: 'captionBold' }),
          // Grid layout: icon row (44px) + label row (auto, wraps)
          // 44px = 36px icon bg + 4px top + 4px bottom spacing
          'grid h-auto min-h-16 w-full grid-cols-1 grid-rows-[44px_auto] place-items-center gap-0 rounded-[10px] p-0',
          // Icon background - ::before pseudo-element
          // top-1 (4px) centers 36px bg in 44px row: (44-36)/2 = 4px
          // Horizontal centering uses inset-x-0 + mx-auto (translate-free, RTL-safe)
          'before:inset-x-0 before:top-1 before:mx-auto before:size-9',
          // Fill + icon colour must transition with identical timing — a
          // one-sided transition makes the fill snap while the icon eases
          'before:transition-colors before:duration-200',
          'motion-reduce:before:transition-none',
          // Icon transition
          '[&>svg]:transition-colors [&>svg]:duration-200',
          '[&>svg]:motion-reduce:transition-none',
          // Localized labels can exceed the narrow rail: hyphens-auto breaks an
          // over-long word at a syllable (reads better than break-words' mid-word
          // orphan); text-balance evens multi-word wraps.
          '[&>span:last-child]:w-full [&>span:last-child]:self-start [&>span:last-child]:text-center',
          '[&>span:last-child]:hyphens-auto [&>span:last-child]:whitespace-normal [&>span:last-child]:text-balance [&>span:last-child]:break-words [&>span:last-child]:text-[10px]',
          '[&>span:last-child]:leading-tight [&>span:last-child]:text-text-tertiary'
        ),
        iconOnly: cn(
          // Compact 36px button — matches before:size-9 in default layout
          'mx-auto flex size-9 items-center justify-center',
          // No ::before needed — button IS the visual area, use direct bg
          'before:hidden',
          // Border-radius for hover bg and focus ring
          'rounded-[10px]'
        ),
      },
      variant: {
        default: cn(
          'hover:before:bg-background-secondary-hover',
          'focus-visible:before:bg-background-secondary-hover',
          'active:before:bg-background-secondary-pressed',
          // Icon colours on :active (mousedown) — the same instant the fill
          // goes pressed — rather than one render later when data-active flips
          '[&:active>svg]:text-text-primary',
          'data-[active=true]:before:bg-background-secondary-pressed',
          'data-[state=open]:before:bg-background-secondary-pressed',
          'data-[state=open]:hover:before:bg-background-secondary-hover'
        ),
        emphasis: cn(
          'hover:before:bg-background-secondary-hover',
          'focus-visible:before:bg-background-secondary-hover',
          'active:before:bg-background-secondary-pressed',
          '[&:active>svg]:text-text-primary',
          'data-[active=true]:before:bg-background-secondary-pressed',
          'data-[state=open]:before:bg-background-secondary-pressed',
          'data-[state=open]:hover:before:bg-background-secondary-hover'
        ),
        action: cn(
          'before:bg-background-button-primary',
          'hover:before:bg-background-button-primary-hover',
          'focus-visible:before:bg-background-button-primary-hover',
          'active:before:bg-background-button-primary-pressed',
          'data-[state=open]:before:bg-background-button-primary-pressed',
          'data-[state=open]:hover:before:bg-background-button-primary-hover',
          'disabled:before:bg-background-button-primary-disabled',
          '[&>svg]:text-text-primary-invert'
        ),
      },
    },
    compoundVariants: [
      // Default layout: icon color on active (label stays text-text-tertiary).
      // Uses a single arbitrary variant [&[data-active=true]>svg] instead of
      // stacking data-[active=true]: on [&>svg]: — the stacked form compiles
      // but loses the cascade against the sidebar's inherited text color.
      {
        layout: 'default',
        variant: 'default',
        className: '[&[data-active=true]>svg]:text-text-primary',
      },
      {
        layout: 'default',
        variant: 'emphasis',
        className: '[&[data-active=true]>svg]:text-text-primary',
      },
      // IconOnly layout: direct bg instead of ::before (button IS the visual area).
      // Uses aria-expanded: instead of data-[state=open]: because when wrapped in
      // TooltipTrigger + DropdownMenuTrigger, Tooltip's data-state="closed" overwrites
      // Dropdown's data-state="open". aria-expanded is only set by DropdownMenuTrigger.
      {
        layout: 'iconOnly',
        variant: ['default', 'emphasis'],
        className: cn(
          'hover:bg-background-secondary-hover',
          'focus-visible:bg-background-secondary-hover',
          'active:bg-background-secondary-pressed',
          'data-[active=true]:bg-background-secondary-pressed',
          'aria-expanded:bg-background-secondary-pressed',
          'aria-expanded:hover:bg-background-secondary-hover',
          '[&[data-active=true]>svg]:text-text-primary',
          '[&[aria-expanded=true]>svg]:text-text-primary'
        ),
      },
      {
        layout: 'iconOnly',
        variant: 'action',
        className: cn(
          'bg-background-button-primary',
          'hover:bg-background-button-primary-hover',
          'focus-visible:bg-background-button-primary-hover',
          'active:bg-background-button-primary-pressed',
          'aria-expanded:bg-background-button-primary-pressed',
          'aria-expanded:hover:bg-background-button-primary-hover',
          'disabled:bg-background-button-primary-disabled'
        ),
      },
    ],
    defaultVariants: {
      layout: 'default',
      variant: 'default',
    },
  }
);

// ============================================================================
// Type Exports
// ============================================================================

export type SidebarMenuButtonLabelIconsVariants = VariantProps<
  typeof sidebarMenuButtonLabelIconsVariants
>;
export type SidebarGapVariants = VariantProps<typeof sidebarGapVariants>;
export type SidebarContainerVariants = VariantProps<typeof sidebarContainerVariants>;
export type SidebarInnerVariants = VariantProps<typeof sidebarInnerVariants>;
export type SidebarRailVariants = VariantProps<typeof sidebarRailVariants>;
export type SidebarFooterVariants = VariantProps<typeof sidebarFooterVariants>;
export type SidebarActionVariants = VariantProps<typeof sidebarActionVariants>;
export type SidebarContentVariants = VariantProps<typeof sidebarContentVariants>;
export type SidebarGroupLabelVariants = VariantProps<typeof sidebarGroupLabelVariants>;
export type SidebarGroupActionVariants = VariantProps<typeof sidebarGroupActionVariants>;
export type SidebarMenuButtonVariants = VariantProps<typeof sidebarMenuButtonVariants>;
export type SidebarMenuActionVariants = VariantProps<typeof sidebarMenuActionVariants>;
export type SidebarMenuBadgeVariants = VariantProps<typeof sidebarMenuBadgeVariants>;
export type SidebarMenuSubVariants = VariantProps<typeof sidebarMenuSubVariants>;
export type SidebarMenuSubButtonVariants = VariantProps<
  typeof sidebarMenuSubButtonVariants
>;
export type SidebarMenuVariants = VariantProps<typeof sidebarMenuVariants>;
